/**
 * Trucks: a rigid box truck, and an articulated one, a tractor with a box
 * semi-trailer. Both are built as `VehicleModel`s, as cars are, so they are
 * driven, parked, baked for traffic and boarded the same way.
 *
 * Both are cab-overs, as European trucks are: a tall flat-fronted cab over
 * the front axle, the driver high up behind a deep windscreen. The cab is a
 * frame (a lower body, pillars, a roof and glass) so the driver can be seen
 * sitting in it, and can see out from it. The box truck's wheelbase is 4.8 m;
 * the tractor's 3.7 m, its fifth wheel just ahead of its rear axle, carrying
 * a 13.6 m trailer on a tri-axle bogie near its tail: 16.7 m in all, as the
 * law allows in Europe.
 *
 * Positions are metres in the vehicle's frame: x right, y up from the
 * ground, z forward-negative, the origin at the middle of the (tractor's)
 * wheelbase, as a car's is.
 */

import { BoxGeometry, CylinderGeometry, Group, Mesh, SpotLight, TorusGeometry } from 'three';
import type { MeshStandardMaterial } from 'three';
import type { TrailerModel, TruckKind, VehicleModel, VehicleWheel } from './Vehicle.js';
import {
  buildWheel, chrome, clusterGeometry, clusterMaterial, dash, faceted, glass, headlamp, indicator, inside, part, plate,
  plateBlue, taillamp, trim, upholstery,
} from './Vehicle.js';

/** Dimensions of each truck, metres. */
export const TRUCKS = {
  truck: { front: -4.3, cabLength: 2.15, roof: 3.15, frontAxle: -2.95, rearAxle: 1.85, rearEnd: 4.3, wheelRadius: 0.5 },
  semi: { front: -3.0, cabLength: 2.35, roof: 3.45, frontAxle: -1.85, rearAxle: 1.85, rearEnd: 2.95, wheelRadius: 0.5 },
} as const;
/** Width of a cab, and of a load body or trailer. */
const CAB_WIDTH = 2.45;
const BOX_WIDTH = 2.55;
/** The kingpin on the tractor, and the trailer from it. */
export const SEMI_TRAILER = { kingpinZ: 1.45, front: -1.2, length: 13.6, axles: [8.6, 9.9, 11.2], wheelRadius: 0.45, floor: 1.25, top: 4.0 } as const;

const box = (w: number, h: number, d: number, material: MeshStandardMaterial, x: number, y: number, z: number) =>
  part(new BoxGeometry(w, h, d), material, x, y, z);

/** The cab: a framed box over the front axle, with its glass, lamps and the driver's place inside. */
function buildCab(body: Group, paint: MeshStandardMaterial, front: number, length: number, roof: number) {
  const w = CAB_WIDTH, half = w / 2;
  const floor = 0.95, belt = 1.95, back = front + length;
  const mid = front + length / 2;
  // Below the windows: the cab's lower body.
  body.add(box(w, belt - floor, length, paint, 0, (belt + floor) / 2, mid));
  // The roof, and the back wall up to it.
  body.add(box(w, roof - 2.95, length, paint, 0, (roof + 2.95) / 2, mid));
  body.add(box(w, 2.95 - belt, 0.08, paint, 0, (2.95 + belt) / 2, back - 0.04));
  // Pillars at the four corners of the glasshouse.
  for (const x of [-half + 0.05, half - 0.05]) {
    for (const z of [front + 0.05, back - 0.1]) body.add(box(0.1, 2.95 - belt, 0.1, paint, x, (2.95 + belt) / 2, z));
  }
  // Glass: the deep windscreen and a side window each side.
  body.add(box(w - 0.2, 2.95 - belt - 0.02, 0.03, glass, 0, (2.95 + belt) / 2, front + 0.02));
  for (const side of [-1, 1]) body.add(box(0.03, 2.95 - belt - 0.06, length - 0.25, glass, side * (half - 0.02), (2.95 + belt) / 2, mid + 0.05));
  // The face: grille, bumper, lamps, plate, a sun visor over the glass.
  body.add(box(1.7, 0.65, 0.05, trim, 0, 1.5, front - 0.02));
  for (let i = 0; i < 4; i++) body.add(box(1.6, 0.04, 0.06, chrome, 0, 1.28 + i * 0.15, front - 0.04));
  body.add(box(w + 0.05, 0.38, 0.28, trim, 0, 0.66, front + 0.05));
  for (const side of [-1, 1] as const) {
    body.add(box(0.42, 0.2, 0.06, headlamp, side * 0.86, 0.98, front - 0.03));
    body.add(box(0.14, 0.1, 0.06, indicator, side * 1.12, 0.98, front - 0.03));
    // Mirrors on arms, out ahead of the doors.
    body.add(box(0.05, 0.05, 0.5, trim, side * (half + 0.12), 2.75, front + 0.35));
    body.add(box(0.1, 0.5, 0.28, trim, side * (half + 0.32), 2.45, front + 0.12));
    // Steps up to the door.
    for (const y of [0.62, 1.0]) body.add(box(0.3, 0.05, 0.5, trim, side * (half - 0.1), y, mid - 0.15));
  }
  body.add(box(w + 0.02, 0.12, 0.4, paint, 0, 2.98, front - 0.15));
  body.add(box(0.52, 0.11, 0.02, plate, 0, 0.66, front - 0.1));
  body.add(box(0.05, 0.11, 0.022, plateBlue, -0.235, 0.66, front - 0.105));
  // Amber marker lamps along the visor.
  for (const x of [-0.6, -0.2, 0.2, 0.6]) body.add(box(0.12, 0.06, 0.06, indicator, x, 3.07, front - 0.36));

  // Inside: the dash, two seats, the wheel, the instruments.
  const seatX = -0.55, hipY = 1.85, hipZ = mid + 0.15;
  const wheelY = 2.2, wheelZ = front + 0.55;
  body.add(inside(new BoxGeometry(w - 0.2, 0.32, 0.45), dash, 0, belt - 0.06, front + 0.3));
  for (const x of [seatX, -seatX]) {
    body.add(inside(new BoxGeometry(0.55, 0.14, 0.55), upholstery, x, hipY - 0.1, hipZ));
    const seatBack = inside(new BoxGeometry(0.52, 0.7, 0.12), upholstery, x, hipY + 0.3, hipZ + 0.3);
    seatBack.rotation.x = 0.15;
    body.add(seatBack);
  }
  // A truck's wheel is big and lies nearly flat.
  const steering = inside(new TorusGeometry(0.24, 0.03, 5, 14), dash, seatX, wheelY, wheelZ);
  steering.rotation.x = -1.05;
  body.add(steering);
  const cluster = new Mesh(clusterGeometry, clusterMaterial);
  cluster.position.set(seatX, wheelY + 0.1, wheelZ - 0.22);
  cluster.rotation.x = -0.35;
  cluster.userData.interior = true;
  cluster.visible = false;
  body.add(cluster);

  // The driver's door: a panel hinged at its front edge, shown only while it swings.
  const door = new Group();
  door.visible = false;
  door.position.set(-half - 0.01, 0, front + 0.25);
  const doorLength = length - 0.55;
  door.add(part(new BoxGeometry(0.05, 1.9, doorLength), paint, 0, 1.95, doorLength / 2));
  door.add(part(new BoxGeometry(0.02, 0.85, doorLength - 0.15), glass, 0.01, 2.45, doorLength / 2));
  body.add(door);

  const headlights = ([-1, 1] as const).map((side) => {
    const beam = new SpotLight(0xfff0d0, 0, 70, 0.42, 0.6, 1.4);
    beam.position.set(side * 0.86, 0.98, front - 0.06);
    beam.target.position.set(side * 0.86 + side * 1.6, 0, front - 24);
    body.add(beam, beam.target);
    return beam;
  }) as [SpotLight, SpotLight];

  return {
    door, cluster, headlights,
    seat: { hipY, hipZ, wheelY, wheelZ, pedalY: floor + 0.1, pedalZ: front + 0.35, x: seatX, outsideX: -half - 0.6 },
  };
}

/** Two wheels on an axle at `z`, `x` either side of the middle. */
function axle(root: Group, z: number, x: number, r: number, front: boolean, into: VehicleWheel[]): void {
  for (const side of [-1, 1] as const) {
    const pivot = new Group();
    pivot.position.set(side * x, r, z);
    const spin = buildWheel(side, r);
    // Twin tyres on a driven or trailer axle: wider, as a pair of tyres looks.
    if (!front) spin.scale.x = 1.8;
    pivot.add(spin);
    root.add(pivot);
    into.push({ pivot, spin, front, x: side * x, z });
  }
}

/** Mudguards over a pair of wheels. */
function guards(body: Group, z: number, x: number, r: number): void {
  for (const side of [-1, 1]) body.add(box(0.5, 0.06, r * 2.4, trim, side * x, r * 2 + 0.12, z));
}

/** A box body or a trailer's box, with its rear doors and lamps at `tail`. */
function loadBox(group: Group, panel: MeshStandardMaterial, z0: number, z1: number, floor: number, top: number, width: number): void {
  const length = z1 - z0;
  group.add(box(width, top - floor, length, panel, 0, (floor + top) / 2, (z0 + z1) / 2));
  // A dark rail along the top and bottom edges, and the rear doors' frame.
  for (const side of [-1, 1]) {
    group.add(box(0.03, 0.1, length, trim, side * (width / 2 + 0.01), floor + 0.05, (z0 + z1) / 2));
    group.add(box(0.03, 0.08, length, trim, side * (width / 2 + 0.01), top - 0.04, (z0 + z1) / 2));
  }
  // The rear doors, pale as the box: a dark seam down the middle and the
  // lock bars on each door.
  group.add(box(width - 0.1, top - floor - 0.12, 0.03, panel, 0, (floor + top) / 2, z1 + 0.01));
  group.add(box(0.03, top - floor - 0.14, 0.04, trim, 0, (floor + top) / 2, z1 + 0.03));
  for (const x of [-0.75, -0.3, 0.3, 0.75]) group.add(box(0.035, top - floor - 0.3, 0.04, chrome, x, (floor + top) / 2, z1 + 0.035));
  // Lamps and the under-run bar beneath the tail.
  for (const side of [-1, 1] as const) {
    group.add(box(0.34, 0.16, 0.06, taillamp, side * (width / 2 - 0.35), floor - 0.3, z1 - 0.02));
    group.add(box(0.12, 0.16, 0.06, indicator, side * (width / 2 - 0.12), floor - 0.3, z1 - 0.02));
  }
  group.add(box(width - 0.3, 0.12, 0.12, trim, 0, 0.55, z1 - 0.1));
  group.add(box(0.52, 0.11, 0.02, plate, 0, floor - 0.32, z1 + 0.01));
}

/**
 * Build a truck of a kind and colour: the cab in the colour, the load body
 * pale, as fleets mostly are. The root's origin is on the ground at the
 * middle of its (tractor's) wheelbase. The `open` cab is the same either way.
 */
export function createTruck(kind: TruckKind, colour: number, _open = true): VehicleModel {
  const spec = TRUCKS[kind];
  const root = new Group();
  const body = new Group();
  root.add(body);
  const paint = faceted(colour, 0.35, 0.2);
  const panel = faceted(0xe8e8e3, 0.6, 0.05);
  const r = spec.wheelRadius;
  const cab = buildCab(body, paint, spec.front, spec.cabLength, spec.roof);
  const back = spec.front + spec.cabLength;

  // The chassis: two rails from behind the cab to the tail.
  body.add(box(1.0, 0.3, spec.rearEnd - back, trim, 0, 0.85, (back + spec.rearEnd) / 2));
  // Fuel tank between the axles, battery box opposite.
  body.add(part(new CylinderGeometry(0.3, 0.3, 1.2, 10).rotateX(Math.PI / 2), chrome, 1.0, 0.75, (spec.frontAxle + spec.rearAxle) / 2 - 0.2));
  body.add(box(0.45, 0.5, 0.7, trim, -1.0, 0.75, (spec.frontAxle + spec.rearAxle) / 2 - 0.2));
  guards(body, spec.frontAxle, 1.03, r);
  guards(body, spec.rearAxle, 1.03, r);

  const wheels: VehicleWheel[] = [];
  axle(root, spec.frontAxle, 1.03, r, true, wheels);
  axle(root, spec.rearAxle, 1.0, r, false, wheels);

  let trailer: TrailerModel | undefined;
  if (kind === 'truck') {
    // The load body: a box from just behind the cab to the tail.
    loadBox(body, panel, back + 0.15, spec.rearEnd, 1.1, 3.75, BOX_WIDTH);
  } else {
    // The fifth wheel, an exhaust stack behind the cab, a deflector on the roof.
    body.add(box(1.6, 0.12, 1.4, trim, 0, 1.12, SEMI_TRAILER.kingpinZ));
    body.add(part(new CylinderGeometry(0.08, 0.08, 2.2, 8), chrome, 0.95, 2.4, back + 0.15));
    body.add(box(CAB_WIDTH - 0.3, 0.35, 1.2, paint, 0, spec.roof + 0.17, spec.front + 1.1));
    trailer = buildTrailer(root, paint, panel);
  }

  return {
    kind, root, body, wheels, wheelRadius: r, headlights: cab.headlights, paint, door: cab.door, cluster: cab.cluster,
    seat: cab.seat, trailer,
  };
}

/** The semi-trailer, its root at the kingpin. */
function buildTrailer(tractor: Group, paint: MeshStandardMaterial, panel: MeshStandardMaterial): TrailerModel {
  const t = SEMI_TRAILER;
  const root = new Group();
  root.position.set(0, 0, t.kingpinZ);
  tractor.add(root);
  const tail = t.front + t.length;
  loadBox(root, panel, t.front, tail, t.floor, t.top, BOX_WIDTH);
  // A stripe of the tractor's colour along each side.
  for (const side of [-1, 1]) root.add(box(0.02, 0.35, t.length - 0.4, paint, side * (BOX_WIDTH / 2 + 0.015), t.floor + 0.55, t.front + t.length / 2));
  // The underframe, the landing legs wound up, and the side guards.
  root.add(box(1.0, 0.3, t.length - 0.4, trim, 0, t.floor - 0.15, t.front + t.length / 2));
  for (const side of [-1, 1]) {
    root.add(box(0.12, 0.85, 0.12, trim, side * 0.9, t.floor - 0.6, 1.4));
    root.add(box(0.03, 0.3, 6.2, trim, side * (BOX_WIDTH / 2 - 0.02), 0.78, 4.6));
  }
  const wheels: VehicleWheel[] = [];
  for (const z of t.axles) {
    axle(root, z, 1.02, t.wheelRadius, false, wheels);
    guards(root, z, 1.02, t.wheelRadius);
  }
  return { root, wheels, wheelRadius: t.wheelRadius, kingpinZ: t.kingpinZ, axleZ: t.axles[1], length: tail, width: BOX_WIDTH };
}
